import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, Settings2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { getStoredCookieConsent } from '@/lib/cookie-consent';

type Preference = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
};

const ANALYTICS_AVAILABLE = Boolean(
  (import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined)?.trim()
);

export function openCookiePreferences() {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent('regionalumzug24:open-cookie-preferences'));
}

export function CookieConsent() {
  const { t } = useTranslation();
  const { consent, accept } = useCookieConsent();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferences, setPreferences] = useState<Preference>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    function openFromFooter() {
      setPreferencesOpen(true);
    }
    window.addEventListener('regionalumzug24:open-cookie-preferences', openFromFooter);
    return () =>
      window.removeEventListener('regionalumzug24:open-cookie-preferences', openFromFooter);
  }, []);

  const showBanner = consent === null;

  useEffect(() => {
    if (preferencesOpen) {
      const stored = getStoredCookieConsent();
      setPreferences({
        essential: true,
        analytics: stored === 'all' && ANALYTICS_AVAILABLE,
        marketing: false,
      });
    }
  }, [preferencesOpen]);

  const savePreferences = () => {
    if (preferences.analytics) {
      accept('all');
    } else {
      accept('essential');
    }
    setPreferencesOpen(false);
  };

  const acceptAll = () => {
    accept('all');
    setPreferencesOpen(false);
  };

  const acceptEssential = () => {
    accept('essential');
    setPreferencesOpen(false);
  };

  const labels = useMemo(
    () => ({
      title: t('cookieConsent.title'),
      message: t('cookieConsent.message'),
      acceptAll: t('cookieConsent.acceptAll'),
      essentialOnly: t('cookieConsent.essentialOnly'),
      settings: t('cookieConsent.settings'),
      preferencesTitle: t('cookieConsent.preferencesTitle'),
      preferencesDescription: t('cookieConsent.preferencesDescription'),
      essentialTitle: t('cookieConsent.essentialTitle'),
      essentialDescription: t('cookieConsent.essentialDescription'),
      analyticsTitle: t('cookieConsent.analyticsTitle'),
      analyticsDescription: t('cookieConsent.analyticsDescription'),
      marketingTitle: t('cookieConsent.marketingTitle'),
      marketingDescription: t('cookieConsent.marketingDescription'),
      savePreferences: t('cookieConsent.savePreferences'),
      cookiePolicyLink: t('cookieConsent.cookiePolicyLink'),
      close: t('cookieConsent.close'),
      footerLink: t('cookieConsent.footerLink'),
    }),
    [t]
  );

  if (!showBanner && !preferencesOpen) {
    return null;
  }

  return (
    <>
      {showBanner && (
        <div
          role="region"
          aria-label={labels.title}
          className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 p-3 sm:p-4 shadow-lg"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex items-start gap-3">
              <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <div className="text-sm">
                <p className="font-medium leading-tight">{labels.title}</p>
                <p className="text-muted-foreground leading-snug mt-1">{labels.message}</p>
                <a
                  href="/datenschutz"
                  className="mt-1 inline-block text-xs text-primary underline-offset-2 hover:underline"
                >
                  {labels.cookiePolicyLink}
                </a>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreferencesOpen(true)}
                aria-label={labels.settings}
              >
                <Settings2 className="h-4 w-4" aria-hidden="true" />
                {labels.settings}
              </Button>
              <Button variant="secondary" size="sm" onClick={acceptEssential}>
                {labels.essentialOnly}
              </Button>
              <Button size="sm" onClick={acceptAll}>
                {labels.acceptAll}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
        <DialogContent className="max-w-xl" aria-describedby="cookie-preferences-description">
          <DialogHeader>
            <DialogTitle>{labels.preferencesTitle}</DialogTitle>
            <DialogDescription id="cookie-preferences-description">
              {labels.preferencesDescription}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{labels.essentialTitle}</CardTitle>
                    <CardDescription>{labels.essentialDescription}</CardDescription>
                  </div>
                  <Switch checked disabled aria-readonly />
                </div>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{labels.analyticsTitle}</CardTitle>
                    <CardDescription>
                      {ANALYTICS_AVAILABLE
                        ? labels.analyticsDescription
                        : t('cookieConsent.analyticsUnavailable')}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="analytics-toggle" className="sr-only">
                      {labels.analyticsTitle}
                    </Label>
                    <Switch
                      id="analytics-toggle"
                      checked={preferences.analytics}
                      disabled={!ANALYTICS_AVAILABLE}
                      onCheckedChange={(checked) =>
                        setPreferences((prev) => ({ ...prev, analytics: Boolean(checked) }))
                      }
                    />
                  </div>
                </div>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{labels.marketingTitle}</CardTitle>
                    <CardDescription>{labels.marketingDescription}</CardDescription>
                  </div>
                  <Switch checked={false} disabled aria-readonly />
                </div>
              </CardHeader>
            </Card>
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
            <a
              href="/datenschutz"
              className="text-xs text-muted-foreground underline-offset-2 hover:underline self-center sm:self-auto"
            >
              {labels.cookiePolicyLink}
            </a>
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <Button variant="outline" onClick={() => setPreferencesOpen(false)}>
                <X className="h-4 w-4" aria-hidden="true" />
                {labels.close}
              </Button>
              <Button variant="secondary" onClick={acceptEssential}>
                {labels.essentialOnly}
              </Button>
              <Button onClick={savePreferences}>{labels.savePreferences}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
